'use client';

import Link from 'next/link';
import { useTranslations, useFormatter } from 'next-intl';
import { Search } from 'lucide-react';
import {
  type PaginatedResponse,
  type LeaderboardParticipant,
  type LeaderboardPillion,
} from '@impact/types';
import { formatPublicCurrency } from '@/lib/format/currency-display';
import { Flag } from '@/components/ui/flag/flag';
import { RankBadge } from './RankBadge';

export function EntrantLeaderboardTable({
  data,
  isLoading,
  currency,
  highlightEntrantId,
  fallbackImageUrl,
  rankBadges,
}: {
  data: PaginatedResponse<LeaderboardParticipant | LeaderboardPillion> | undefined;
  isLoading: boolean;
  currency: string;
  highlightEntrantId?: string;
  fallbackImageUrl?: string | null;
  rankBadges?: Record<number, string>;
}) {
  const t = useTranslations('leaderboard');
  const format = useFormatter();

  if (isLoading) {
    return (
      <div className="divide-y divide-stone-100">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="p-4 md:px-6 md:py-4 animate-pulse">
            {/* Mobile skeleton */}
            <div className="flex items-center gap-3 md:hidden min-h-[60px]">
              <div className="w-8 h-8 rounded-full bg-stone-200 flex-shrink-0" />
              <div className="w-10 h-10 rounded-full bg-stone-200 flex-shrink-0" />
              <div className="flex-1 min-w-0 space-y-1.5">
                <div className="h-4 w-28 bg-stone-200 rounded" />
                <div className="h-3 w-20 bg-stone-100 rounded" />
              </div>
              <div className="text-right space-y-1.5 flex-shrink-0">
                <div className="h-5 w-16 bg-stone-200 rounded ml-auto" />
                <div className="h-3 w-10 bg-stone-100 rounded ml-auto" />
              </div>
            </div>
            {/* Desktop skeleton */}
            <div className="hidden md:grid md:grid-cols-12 md:gap-4 md:items-center">
              <div className="col-span-1">
                <div className="w-8 h-8 rounded-full bg-stone-200" />
              </div>
              <div className="col-span-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-stone-200" />
                <div className="space-y-2">
                  <div className="h-4 w-32 bg-stone-200 rounded" />
                  <div className="h-3 w-20 bg-stone-100 rounded" />
                </div>
              </div>
              <div className="col-span-2">
                <div className="h-4 w-24 bg-stone-100 rounded" />
              </div>
              <div className="col-span-2">
                <div className="h-4 w-24 bg-stone-100 rounded" />
              </div>
              <div className="col-span-2 text-right">
                <div className="h-4 w-10 bg-stone-100 rounded ml-auto" />
              </div>
              <div className="col-span-2 text-right">
                <div className="h-6 w-16 bg-stone-200 rounded ml-auto" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  const items = data?.items ?? [];

  if (items.length === 0) {
    return (
      <div className="p-12 text-center">
        <Search className="w-12 h-12 text-stone-300 mx-auto mb-4" />
        <p className="text-stone-600">{t('noResults')}</p>
        <p className="text-stone-400 text-sm mt-1">{t('tryDifferentSearch')}</p>
      </div>
    );
  }

  return (
    <>
      <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-stone-50 text-stone-500 text-sm font-medium border-b border-stone-200">
        <div className="col-span-1">{t('colRank')}</div>
        <div className="col-span-3">{t('colParticipant')}</div>
        <div className="col-span-2">{t('colCountry')}</div>
        <div className="col-span-2">{t('colCity')}</div>
        <div className="col-span-2 text-right">{t('colDonors')}</div>
        <div className="col-span-2 text-right">{t('colRaised')}</div>
      </div>
      <div className="divide-y divide-stone-100">
        {items.map((entrant) => {
          const locationName = 'cityName' in entrant ? entrant.cityName : entrant.eventName;
          const isPassenger = 'isPassenger' in entrant ? entrant.isPassenger : true;
          const subtitle = isPassenger
            ? t('passenger')
            : entrant.username
              ? `@${entrant.username}`
              : '';

          return (
            <Link
              key={entrant.id}
              href={entrant.username ? `/fundraiser/${entrant.username}` : '#'}
              className={`block p-4 md:px-6 md:py-4 hover:bg-stone-50 active:bg-stone-100 transition-colors ${highlightEntrantId === entrant.id ? 'bg-primary/10 ring-1 ring-inset ring-primary/20' : ''}`}
            >
              {/* Mobile card layout */}
              <div className="md:hidden">
                <div className="flex items-center gap-3 min-h-[60px]">
                  <RankBadge rank={entrant.rank} badges={rankBadges} />
                  <div className="w-10 h-10 rounded-full bg-stone-200 overflow-hidden flex-shrink-0">
                    {entrant.profileImageUrl || fallbackImageUrl ? (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img
                        src={(entrant.profileImageUrl || fallbackImageUrl)!}
                        alt={entrant.name}
                        className="object-cover w-full h-full"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-stone-500 font-medium text-sm">
                        {entrant.name
                          .split(' ')
                          .map((n) => n[0])
                          .join('')}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-stone-900 font-medium truncate text-sm">{entrant.name}</p>
                    <p className="text-stone-500 text-xs truncate">
                      {subtitle}
                      {(entrant.countryName || locationName) && (
                        <>
                          {subtitle && ' · '}
                          {[locationName, entrant.countryName].filter(Boolean).join(', ')}
                        </>
                      )}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-base font-bold text-primary-strong">
                      {formatPublicCurrency(format, entrant.raised, currency)}
                    </p>
                    <p className="text-stone-500 text-xs">
                      {entrant.donors} {t('colDonors').toLowerCase()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Desktop layout */}
              <div className="hidden md:grid md:grid-cols-12 md:gap-4 md:items-center">
                <div className="col-span-1 flex items-center gap-2">
                  <RankBadge rank={entrant.rank} badges={rankBadges} />
                </div>
                <div className="col-span-3 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-stone-200 overflow-hidden flex-shrink-0">
                    {entrant.profileImageUrl || fallbackImageUrl ? (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img
                        src={(entrant.profileImageUrl || fallbackImageUrl)!}
                        alt={entrant.name}
                        className="object-cover w-full h-full"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-stone-500 font-medium">
                        {entrant.name
                          .split(' ')
                          .map((n) => n[0])
                          .join('')}
                      </div>
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-stone-900 font-medium truncate">{entrant.name}</p>
                    {isPassenger ? (
                      <p className="text-stone-500 text-sm">{t('passenger')}</p>
                    ) : (
                      entrant.username && (
                        <p className="text-stone-500 text-sm">@{entrant.username}</p>
                      )
                    )}
                  </div>
                </div>
                <div className="col-span-2 flex items-center gap-2">
                  <Flag code={entrant.countryCode} size="sm" />
                  <p className="text-stone-600 text-sm">{entrant.countryName}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-stone-600 text-sm truncate">{locationName}</p>
                </div>
                <div className="col-span-2 text-right">
                  <p className="text-stone-600">{entrant.donors}</p>
                </div>
                <div className="col-span-2 text-right">
                  <p className="text-xl font-bold text-primary-strong">
                    {formatPublicCurrency(format, entrant.raised, currency)}
                  </p>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </>
  );
}
